import React from 'react';
import SEO from '../components/SEO';
import UtilityPageLayout from '../components/UtilityPageLayout';
import UtilitySection from '../components/UtilitySection';
import { siteInfo } from '../data/siteInfo';

const PrivacyPolicy = () => {
  const companyName = siteInfo.companyName;
  const privacyEmail = siteInfo.legal.privacyEmail;
  const privacyMail = `mailto:${privacyEmail}`;

  return (
    <>
      <SEO
        title={`Privacy Policy | ${companyName}`}
        description={`${companyName} privacy policy — how we collect, use and protect your personal data in accordance with UK GDPR.`}
      />

      <UtilityPageLayout eyebrow="Legal" title="Privacy Policy" meta={`Last updated: ${siteInfo.legal.legalUpdated}`}>
        <UtilitySection title="1. Who We Are">
          <p>
            {companyName} (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;) is a registered property company operating in the United Kingdom. We are registered with the Information Commissioner&apos;s Office (ICO).
          </p>
          <p>
            For all data protection queries, contact our Data Controller at:{' '}
            <a href={privacyMail}>{privacyEmail}</a>
          </p>
        </UtilitySection>

        <UtilitySection title="2. What Data We Collect">
          <p>We may collect the following categories of personal data:</p>
          <ul>
            <li>
              <strong>Identity data:</strong> name, username or similar identifier
            </li>
            <li>
              <strong>Contact data:</strong> postal address, email address, telephone numbers
            </li>
            <li>
              <strong>Transaction data:</strong> details of enquiries, viewings booked and services received
            </li>
            <li>
              <strong>Technical data:</strong> IP address, browser type, operating system, referring URLs
            </li>
            <li>
              <strong>Usage data:</strong> information about how you use our website
            </li>
            <li>
              <strong>Marketing preferences:</strong> your preferences in receiving marketing communications
            </li>
          </ul>
        </UtilitySection>

        <UtilitySection title="3. How We Use Your Data">
          <p>We use your personal data for the following purposes:</p>
          <ul>
            <li>To register and manage your account or enquiry</li>
            <li>To provide property search, valuation, sales and lettings services</li>
            <li>To communicate with you about properties matching your requirements</li>
            <li>To send marketing communications where you have consented or we have a legitimate interest</li>
            <li>To improve our website and services through analytics</li>
            <li>To comply with legal obligations</li>
          </ul>
        </UtilitySection>

        <UtilitySection title="4. Legal Basis for Processing">
          <p>We process your data under the following lawful bases:</p>
          <ul>
            <li>
              <strong>Contract:</strong> processing necessary to perform our service to you
            </li>
            <li>
              <strong>Legitimate interests:</strong> for marketing to existing and prospective clients
            </li>
            <li>
              <strong>Consent:</strong> for cookies and newsletter subscriptions
            </li>
            <li>
              <strong>Legal obligation:</strong> for Anti-Money Laundering compliance
            </li>
          </ul>
        </UtilitySection>

        <UtilitySection title="5. Data Sharing">
          <p>We may share your personal data with:</p>
          <ul>
            <li>Property portals (Rightmove, Zoopla) where you have enquired about a listing</li>
            <li>Solicitors, mortgage brokers and surveyors involved in your transaction</li>
            <li>Our IT and marketing service providers under data processing agreements</li>
            <li>HM Revenue &amp; Customs and other regulators as required by law</li>
          </ul>
          <p>We do not sell your personal data to third parties.</p>
        </UtilitySection>

        <UtilitySection title="6. Cookies">
          <p>
            We use cookies to enhance your experience. For full details, please see our <a href="/cookie-policy">Cookie Policy</a>. You can manage your cookie preferences at any time using our cookie banner.
          </p>
        </UtilitySection>

        <UtilitySection title="7. Your Rights Under UK GDPR">
          <p>You have the following rights regarding your personal data:</p>
          <ul>
            <li>
              <strong>Right of access (DSAR):</strong> request a copy of your personal data
            </li>
            <li>
              <strong>Right to rectification:</strong> correct inaccurate data
            </li>
            <li>
              <strong>Right to erasure:</strong> request deletion of your data in certain circumstances
            </li>
            <li>
              <strong>Right to data portability:</strong> receive your data in a structured, machine-readable format
            </li>
            <li>
              <strong>Right to object:</strong> object to processing based on legitimate interests or for direct marketing
            </li>
            <li>
              <strong>Right to restrict processing:</strong> request restriction of processing in certain circumstances
            </li>
          </ul>
          <p>
            To exercise any of these rights, contact us at <a href={privacyMail}>{privacyEmail}</a>. We will respond within 30 days.
          </p>
        </UtilitySection>

        <UtilitySection title="8. Data Retention">
          <p>We retain your data for as long as necessary to fulfil the purpose for which it was collected. Specifically:</p>
          <ul>
            <li>Client transaction records: 7 years after completion</li>
            <li>Enquiry and marketing records: 3 years from last contact</li>
            <li>Website analytics data: 26 months</li>
          </ul>
        </UtilitySection>

        <UtilitySection title="9. Data Security">
          <p>
            We implement appropriate technical and organisational security measures to protect your personal data, including SSL encryption, restricted staff access and regular security reviews.
          </p>
        </UtilitySection>

        <UtilitySection title="10. International Transfers">
          <p>
            Some of our service providers may process data outside the UK. Where this occurs, we ensure appropriate safeguards are in place, including UK Standard Contractual Clauses.
          </p>
        </UtilitySection>

        <UtilitySection title="11. Contact & Complaints">
          <p>
            If you are unhappy with how we have handled your personal data, please contact us first at <a href={privacyMail}>{privacyEmail}</a>.
          </p>
          <p>
            You also have the right to complain to the Information Commissioner&apos;s Office (ICO) at{' '}
            <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">
              ico.org.uk
            </a>{' '}
            or by calling 0303 123 1113.
          </p>
        </UtilitySection>
      </UtilityPageLayout>
    </>
  );
};

export default PrivacyPolicy;
